import { App, useAppContext } from "@ftdr/blueprint-components-react";
import { Payment3_paymentmethod_coordinatorJSClient } from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { enTextTemplates } from "./assets/i18n/en-text-templates";
import {
  ErrorNotification,
  PaymentsComponent,
  PaymentsComponentExposedApi,
  PaymentsComponentProps,
} from "./components";
import {
  AgregatedProcessPaymentResponse,
  PaymentProcessorResponse,
} from "./components/payment-processors/types";
import { PaymentMethods } from "./const";
import {
  PaymentContextValueProps,
  PaymentsContext,
} from "./contexts/payment-context";
import classNames from "classnames";
import { validateProps } from "./utils";

export type PaymentsMethodMicroFrontendOwnProps = {
  /**
   * The baseURL to be used for the PaymentsMethodMicroService.for
   */
  baseURL: string;
  /**
   * The token to be used for the PaymentsMethodMicroService.
   */
  token: string;
  /**
   * The tenant service to be used by as a header
   * default: ahs
   */
  tenant?: string;
  /**
   * The hostJsClient to be used for the PaymentsMethodMicroService, when it is passed in props by host .
   */
  hostJsClient?: Payment3_paymentmethod_coordinatorJSClient;
};

export type PaymentsComponentPassthroughProps = Pick<
  PaymentsComponentProps,
  "className" | "cardPaymentProcessor" | "achPaymentProcessor"
> &
  Partial<
    Pick<PaymentProcessorResponse, "onSuccess" | "onFailure" | "onResponse">
  > &
  PaymentContextValueProps;

export type PaymentMethodMicroFrontendProps = PaymentsMethodMicroFrontendOwnProps &
  PaymentsComponentPassthroughProps;

// MFE exposed imperative Api
export type PaymentsMethodMfeExposedApi = PaymentsComponentExposedApi;

export const PaymentMethodMicroFrontend = forwardRef<
  PaymentsMethodMfeExposedApi | undefined,
  PaymentMethodMicroFrontendProps
>(
  (
    {
      // Own prop
      baseURL = "https://dev.apis.frontdoorhome.com/",
      tenant,
      token,
      paymentusIframeBaseUrl = "https://paymentus-iframe.dev.ftdrinternal.com/",
      hostJsClient,
      // IQ Component passthrough props
      applicationName,
      owner,
      initialState,
      state,
      className,
      onStateChange,
      cardPaymentProcessor,
      achPaymentProcessor = "ach",
      onFailure,
      onResponse,
      onSuccess,
      stripeSetup,
      formsSetup,
      defaultOpenPaymentMethod,
      availablePaymentMethods,
      googleAnalytics,
      testIdName,
      styleType,
      setWalletCheckboxProps,
      brand,
      subbrand,
    },
    ref
  ) => {
    const {
      appSettings: { getTextTemplatesByLanguage, getLanguage, localizedText },
    } = useAppContext();

    const paymentsMethodRef = useRef<PaymentsComponentExposedApi>(null);
    const processPaymentResultRef = useRef<
      Promise<AgregatedProcessPaymentResponse | undefined> | undefined
    >(undefined);

    const onClearPaymentResultRef = useCallback(() => {
      processPaymentResultRef.current = undefined;
    }, []);

    useImperativeHandle(ref, () => ({
      processPayment: async (requestData) => {
        if (!processPaymentResultRef.current) {
          processPaymentResultRef.current = paymentsMethodRef?.current?.processPayment(
            requestData
          );
        }
        return await processPaymentResultRef.current
          ?.then()
          .catch()
          .finally(() => {
            onClearPaymentResultRef();
          });
      },
      getClient: (paymentMethod) =>
        paymentsMethodRef?.current?.getClient(paymentMethod),
      getCurrentActivePaymentMethod: () =>
        paymentsMethodRef?.current?.getCurrentActivePaymentMethod(),
      setOpenPaymentMethod: (activePaymentMethod?: PaymentMethods) =>
        paymentsMethodRef?.current?.setOpenPaymentMethod(activePaymentMethod),
    }));

    const contextValue = useMemo<
      PaymentProcessorResponse & PaymentContextValueProps
    >(
      () => ({
        applicationName,
        brand,
        subbrand,
        owner,
        onSuccess: onSuccess ? onSuccess : () => undefined,
        onFailure: onFailure ? onFailure : () => undefined,
        onResponse: onResponse ? onResponse : () => undefined,
        state,
        initialState,
        onStateChange,
        stripeSetup,
        formsSetup,
        defaultOpenPaymentMethod,
        availablePaymentMethods,
        token,
        tenant,
        paymentusIframeBaseUrl,
        baseURL,
        hostJsClient,
        googleAnalytics,
        testIdName,
        styleType,
        setWalletCheckboxProps,
      }),
      [
        applicationName,
        brand,
        subbrand,
        owner,
        tenant,
        token,
        onSuccess,
        onFailure,
        onResponse,
        state,
        onStateChange,
        initialState,
        stripeSetup,
        formsSetup,
        defaultOpenPaymentMethod,
        availablePaymentMethods,
        paymentusIframeBaseUrl,
        baseURL,
        hostJsClient,
        googleAnalytics,
        testIdName,
        styleType,
        setWalletCheckboxProps,
      ]
    );

    let combinedTemplates;
    //returns undefined if the host app doesn't provide an AppContextProvider
    if (getTextTemplatesByLanguage !== undefined) {
      combinedTemplates = {
        ...getTextTemplatesByLanguage(),
        en_US: {
          ...enTextTemplates,
          ...getTextTemplatesByLanguage().en_US,
        },
      };
    } else {
      combinedTemplates = {
        en_US: enTextTemplates,
      };
    }

    return (
      <App
        className="payment-method-micro-frontend"
        appSettings={{
          language: getLanguage ? getLanguage() : "en_US",
          textTemplatesByLanguage: combinedTemplates,
        }}
      >
        {
          // This container div allow us to encapsulate styles in angular applications which is not using custom elements
          // The usage of custom element is not available for this mfe since stripe-elements not support rendering inside shadow root
        }
        <PaymentsContext value={contextValue}>
          {validateProps({
            brand: brand,
            contractID: setWalletCheckboxProps?.contractID,
            customerID: setWalletCheckboxProps?.customerID,
            showWalletCheckbox: setWalletCheckboxProps?.showWalletCheckbox,
          }) ? (
            <PaymentsComponent
              // props based on internal state/ref
              // passthrough props
              className={classNames(styleType || "default", className)}
              cardPaymentProcessor={cardPaymentProcessor}
              achPaymentProcessor={achPaymentProcessor}
              ref={paymentsMethodRef}
            />
          ) : (
            <ErrorNotification
              message={localizedText("REQUIRED_PROPS_ERROR")}
            />
          )}
        </PaymentsContext>
      </App>
    );
  }
);

PaymentMethodMicroFrontend.displayName = "PaymentMethodMicroFrontend";
